import { router, Link } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import AppLayout from '../../components/AppLayout'
import { useTheme } from '../../contexts/ThemeContext'

const CATEGORY_ORDER = ['旬×推し', '人気', '珍しさ指標', 'セットバランス', '重複状況']
function computeMeans(rawScores, threshold, upperLimit, direction) {
  if (!rawScores.length) return { rawMean: 0, adjMean: 0 }
  const rawMean = rawScores.reduce((a, b) => a + b, 0) / rawScores.length
  const range = upperLimit - threshold
  const adjusted = rawScores.map(v => {
    if (range <= 0) return 0
    const ratio = Math.min(1, Math.max(0, (v - threshold) / range))
    return direction === 'positive' ? ratio : 1 - ratio
  })
  const adjMean = adjusted.reduce((a, b) => a + b, 0) / rawScores.length
  return { rawMean, adjMean }
}

const CATEGORY_COLORS = {
  '旬×推し':       '#3a6644',
  '人気':           '#d4762a',
  '珍しさ指標':    '#8a6858',
  'セットバランス': '#5a6e9a',
  '重複状況':       '#7a8a78',
}

function csrfToken() {
  const m = document.querySelector('meta[name="csrf-token"]')
  return m ? m.getAttribute('content') : ''
}

function DistributionChart({ rawScores, threshold, upperLimit, direction, percentiles, C }) {
  const N = 24
  if (!rawScores.length) {
    return <div style={{ height: '128px', display: 'grid', placeItems: 'center', color: C.inkMuted, fontSize: '11px' }}>データなし</div>
  }

  const dataMin = Math.min(...rawScores)
  const dataMax = Math.max(...rawScores)
  const lo  = Math.min(dataMin, threshold, upperLimit)
  const hi  = Math.max(dataMax, threshold, upperLimit)
  const span = Math.max(hi - lo, 1e-6)
  const pad  = span * 0.10
  const xMin = Math.max(0, lo - pad)
  const xMax = hi + pad
  const xSpan = xMax - xMin
  const bucketW = xSpan / N

  const counts = Array(N).fill(0)
  rawScores.forEach(v => {
    const i = Math.min(Math.max(Math.floor((v - xMin) / bucketW), 0), N - 1)
    counts[i]++
  })
  const maxCount = Math.max(...counts, 1)

  const W = 380, H = 120
  const pL = 6, pR = 6, pT = 6, pB = 30
  const cW = W - pL - pR
  const cH = H - pT - pB
  const bW = cW / N

  const toX = v => pL + ((v - xMin) / xSpan) * cW
  const thX = toX(threshold)
  const ulX = toX(upperLimit)

  const n = rawScores.length
  const below   = rawScores.filter(v => v < threshold).length
  const scoring = rawScores.filter(v => v >= threshold && v <= upperLimit).length
  const above   = rawScores.filter(v => v > upperLimit).length

  const { rawMean } = computeMeans(rawScores, threshold, upperLimit, direction)
  const meanX = toX(rawMean)

  const fillColor = direction === 'positive' ? '#4a7c59' : '#c0463a'
  const zoneAlpha = direction === 'positive' ? '25' : '18'

  const pTicks = percentiles ? [
    { k: 'p10', v: percentiles.p10, label: '10%' },
    { k: 'p50', v: percentiles.p50, label: '中央' },
    { k: 'p90', v: percentiles.p90, label: '90%' },
  ].filter(t => t.v != null) : []

  const fmt = v => Math.abs(v) >= 10 ? v.toFixed(1) : v.toFixed(3)

  return (
    <div>
      <svg width={W} height={H} style={{ display: 'block', overflow: 'visible' }}>
        <rect x={Math.max(thX, pL)} y={pT}
          width={Math.max(0, Math.min(ulX, pL + cW) - Math.max(thX, pL))}
          height={cH} fill={fillColor + zoneAlpha} />

        {counts.map((cnt, i) => {
          const x = pL + i * bW
          const bH = (cnt / maxCount) * cH
          const mid = xMin + (i + 0.5) * bucketW
          const inZone = mid >= threshold && mid <= upperLimit
          return (
            <rect key={i} x={x + 1} y={pT + cH - bH} width={bW - 2} height={bH}
              fill={inZone ? fillColor : '#b0b8c4'} opacity={0.75} rx={1} />
          )
        })}

        {pTicks.map(t => (
          <g key={t.k}>
            <line x1={toX(t.v)} y1={pT + cH} x2={toX(t.v)} y2={pT + cH + 4} stroke="#888" strokeWidth={1} />
            <text x={toX(t.v)} y={pT + cH + 11} fontSize={8} fill="#888" textAnchor="middle">{t.label}</text>
          </g>
        ))}

        <line x1={thX} y1={pT} x2={thX} y2={pT + cH} stroke="#c9963f" strokeWidth={2} strokeDasharray="4,2" />
        <line x1={ulX} y1={pT} x2={ulX} y2={pT + cH} stroke="#5b6ba0" strokeWidth={2} strokeDasharray="4,2" />

        <line x1={meanX} y1={pT - 2} x2={meanX} y2={pT + cH} stroke="#2c2c2c" strokeWidth={1.5} />
        <polygon points={`${meanX - 3},${pT - 2} ${meanX + 3},${pT - 2} ${meanX},${pT + 3}`} fill="#2c2c2c" />

        <text x={toX(xMin)} y={H - 4} fontSize={8} fill="#999" textAnchor="start">{fmt(xMin)}</text>
        <text x={toX(dataMin)} y={H - 14} fontSize={8} fill="#666" textAnchor="middle">min {fmt(dataMin)}</text>
        <text x={toX(dataMax)} y={H - 14} fontSize={8} fill="#666" textAnchor="middle">max {fmt(dataMax)}</text>
        <text x={toX(xMax)} y={H - 4} fontSize={8} fill="#999" textAnchor="end">{fmt(xMax)}</text>
      </svg>

      <div style={{ display: 'flex', gap: '10px', fontSize: '10px', marginTop: '3px' }}>
        {direction === 'positive' ? (
          <>
            <span style={{ color: '#c0463a' }}>下限未達 {below}件 ({Math.round(below / n * 100)}%)</span>
            <span style={{ color: fillColor, fontWeight: 700 }}>スコアリング {scoring}件 ({Math.round(scoring / n * 100)}%)</span>
            <span style={{ color: '#5b6ba0' }}>上限超え {above}件 ({Math.round(above / n * 100)}%)</span>
          </>
        ) : (
          <>
            <span style={{ color: fillColor }}>満点 {below}件 ({Math.round(below / n * 100)}%)</span>
            <span style={{ color: '#c9963f', fontWeight: 700 }}>スコアリング {scoring}件 ({Math.round(scoring / n * 100)}%)</span>
            <span style={{ color: '#c0463a' }}>0点 {above}件 ({Math.round(above / n * 100)}%)</span>
          </>
        )}
      </div>
    </div>
  )
}

const RAW_SOURCES = {
  seasonal: {
    source: 'ProductSeasonality.season_status',
    filter: 'season_year = 立案年 / season_month = 配送月（fallback: 同月の最新年）',
    rule: '推し旬 → +1.0 ／ 旬 → +0.5 ／ 避 → −1.0 ／ 通常・空白 → 0',
    aggregate: 'Σ(値) ÷ 品目数',
  },
  popularity: {
    source: 'ProductReview.detail_evaluation（商品名一致）',
    filter: 'is_review_header_row = false ／ detail_evaluation IS NOT NULL ／ ≠ 10 ／ 評価信頼度 ≠ "不可"',
    rule: '商品ごとに avg(detail_evaluation) → (avg − 20) / 20 を [0, 1] にクランプ',
    aggregate: '有効品目の score を単純平均',
  },
  best_one: {
    source: 'ProductItem.best_one_ratio（= best_one_wins / best_one_total）',
    filter: 'best_one_ratio IS NOT NULL ／ best_one_total ≥ 5（信頼度の低い品目を除外）',
    rule: '品目ごとの ratio をそのまま使用',
    aggregate: '有効品目で単純平均（Σ(ratio) ÷ 有効品目数）',
  },
  standard_veggie: {
    source: 'ProductItem.vegtype_flag',
    filter: 'vegtype_flag = "T"（キホン）',
    rule: '該当を 1、それ以外を 0',
    aggregate: '該当数 ÷ 品目数',
  },
  special_veggie: {
    source: 'ProductItem.vegtype_flag',
    filter: 'vegtype_flag = "S"（目玉）',
    rule: '該当を 1、それ以外を 0',
    aggregate: '該当数 ÷ 品目数',
  },
  rare_veggie: {
    source: 'ProductItem.vegtype_flag',
    filter: 'vegtype_flag = "C"（変わり種）',
    rule: '該当を 1、それ以外を 0',
    aggregate: '該当数 ÷ 品目数',
  },
  recent_appearance: {
    source: '過去 7 立案週（隔週は 2,4,6,8,10,12,14 週前）の SetPlanProduct（同 set_type_id）',
    filter: '同一 product_id の登場をカウント（同一 number スロット内の代替候補は先頭のみ）',
    rule: '品目ごとに count = 直近の登場回数、ideal = ProductItem.planning_frequency',
    aggregate: 'Σ(count) ÷ Σ(planning_frequency)',
  },
  previous_overlap: {
    source: '直近 1–2 期前（隔週は 2,4 週前）の SetPlanProduct',
    filter: '同一 product_id かつ need_quantity_* > 0 の曜日が重複 → "同一品目" ／ item_code 先頭2桁一致 → "中分類"（planning_frequency 経過済はスキップ）',
    rule: '同一品目 → 2.0 ／ 中分類 → 1.0 ／ なし → 0',
    aggregate: 'Σ(値) ÷ 品目数',
  },
  color_variety: {
    source: 'ProductItemCategory.item_color（1文字目=color1 / 2文字目=color2）',
    filter: 'NULL 色は除外',
    rule: '全色エントリを集めて unique を数える',
    aggregate: 'unique(色) ÷ 全色エントリ数',
  },
  small_volume: {
    source: 'ProductItemCategory.features → ProductItem.volume',
    filter: 'volume = "小"',
    rule: '該当を 1、それ以外を 0',
    aggregate: '該当数 ÷ 品目数',
  },
  large_volume: {
    source: 'ProductItemCategory.features → ProductItem.volume',
    filter: 'volume = "大"',
    rule: '該当を 1、それ以外を 0',
    aggregate: '該当数 ÷ 品目数',
  },
}

function RawScoreSource({ criterionKey, C }) {
  const info = RAW_SOURCES[criterionKey]
  if (!info) return null
  const Row = ({ label, value, mono }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: '6px', marginBottom: '2px' }}>
      <span style={{ color: C.inkMuted, fontWeight: 600 }}>{label}</span>
      <span style={{
        color: C.ink,
        fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
        fontSize: mono ? '10px' : '11px',
        wordBreak: 'break-word',
      }}>{value}</span>
    </div>
  )
  return (
    <div style={{
      fontSize: '11px', color: C.ink, background: C.paper2,
      border: `1px solid ${C.lineSoft}`, borderRadius: '4px',
      padding: '8px 10px', marginTop: '4px', lineHeight: '1.5',
    }}>
      <div style={{ fontWeight: 700, color: C.ink, marginBottom: '4px', letterSpacing: '0.03em', fontSize: '10px', textTransform: 'uppercase' }}>
        raw_score の出典
      </div>
      <Row label="参照"   value={info.source}    mono />
      <Row label="条件"   value={info.filter}    mono />
      <Row label="変換"   value={info.rule} />
      <Row label="集計"   value={info.aggregate} mono />
    </div>
  )
}

function CriterionCard({ criterion, C }) {
  const [threshold, setThreshold]   = useState(criterion.threshold)
  const [upperLimit, setUpperLimit] = useState(criterion.upper_limit)
  const [saving, setSaving]         = useState(false)

  const isDirty = threshold !== criterion.threshold || upperLimit !== criterion.upper_limit

  const rawScores  = criterion.raw_scores || []
  const dataMax    = rawScores.length ? Math.max(...rawScores) : 1
  const dataMin    = rawScores.length ? Math.min(...rawScores) : 0
  const sliderMax  = Math.max(dataMax * 1.5, upperLimit * 1.3, 1)
  const sliderStep = sliderMax / 200
  const { adjMean } = computeMeans(rawScores, threshold, upperLimit, criterion.direction)

  const warnings = []
  if (rawScores.length) {
    if (criterion.direction === 'positive' && threshold > dataMax)
      warnings.push(`しきい値 ${threshold.toFixed(3)} が実績最大値 ${dataMax.toFixed(3)} を超えています → 全セットが0点`)
    if (criterion.direction === 'negative' && upperLimit < dataMin)
      warnings.push(`上限値 ${upperLimit.toFixed(3)} が実績最小値 ${dataMin.toFixed(3)} を下回っています → 全セットが0点`)
    if (criterion.direction === 'positive' && upperLimit < dataMin)
      warnings.push(`上限値 ${upperLimit.toFixed(3)} が実績最小値 ${dataMin.toFixed(3)} を下回っています → 全セットが満点`)
    const scoring = rawScores.filter(v => v >= threshold && v <= upperLimit).length
    if (rawScores.length > 0 && scoring / rawScores.length < 0.15)
      warnings.push(`スコアリング範囲内のデータが ${scoring}/${rawScores.length} 件のみ（有効に機能していない可能性）`)
  }

  const catColor = CATEGORY_COLORS[criterion.category] || C.accent

  function save() {
    setSaving(true)
    router.patch(`/evaluation_criteria/${criterion.id}`, {
      evaluation_criterion: {
        threshold,
        upper_limit: upperLimit,
        weight_delivery: criterion.weight_delivery,
        weight_appeal:   criterion.weight_appeal,
      },
      return_to: 'index',
    }, {
      preserveScroll: true,
      onFinish: () => setSaving(false),
    })
  }

  function reset() {
    setThreshold(criterion.threshold)
    setUpperLimit(criterion.upper_limit)
  }

  const p = criterion.percentiles
  const suggestion = p && p.p10 != null && p.p90 != null && p.p10 !== p.p90
    ? { threshold: p.p10, upperLimit: p.p90 }
    : null

  function applySuggestion() {
    if (!suggestion) return
    setThreshold(suggestion.threshold)
    setUpperLimit(suggestion.upperLimit)
  }

  const borderColor = isDirty ? catColor : C.line
  const shadow = isDirty ? `0 0 0 2px ${catColor}33, 0 2px 8px rgba(0,0,0,0.08)` : '0 1px 4px rgba(0,0,0,0.06)'

  return (
    <div style={{ background: C.paper, border: `1px solid ${borderColor}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '12px', boxShadow: shadow }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: C.ink }}>{criterion.name}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: C.inkMuted, background: C.paper2, padding: '1px 5px', borderRadius: '3px' }}>{criterion.key}</span>
        <span style={{
          fontSize: '10px', padding: '1px 7px', borderRadius: '3px', fontWeight: 600,
          background: criterion.direction === 'positive' ? '#e8f5ed' : '#fde8e8',
          color:      criterion.direction === 'positive' ? '#4a7c59' : '#c0463a',
        }}>{criterion.direction === 'positive' ? '▲ 加点' : '▼ 減点'}</span>
        <Link href={`/evaluation_criteria/${criterion.id}`} style={{ fontSize: '10px', color: '#64748b', textDecoration: 'none', border: '1px solid #94a3b8', borderRadius: '3px', padding: '1px 7px' }}>履歴</Link>
        <div style={{ flex: 1 }} />
        {suggestion && (
          <button onClick={applySuggestion} title={`推奨: しきい値 ${suggestion.threshold.toFixed(3)} / 上限 ${suggestion.upperLimit.toFixed(3)}`}
            style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '4px', border: `1px dashed ${catColor}`, background: 'transparent', color: catColor, cursor: 'pointer', fontWeight: 600 }}>
            推奨しきい値を適用
          </button>
        )}
        {isDirty && (
          <>
            <button onClick={reset}
              style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '4px', border: `1px solid ${C.line}`, background: 'transparent', color: C.inkMuted, cursor: 'pointer' }}>
              リセット
            </button>
            <button onClick={save} disabled={saving}
              style={{ fontSize: '11px', padding: '4px 14px', borderRadius: '4px', border: 'none', background: catColor, color: '#fff', cursor: saving ? 'default' : 'pointer', fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
              {saving ? '保存中…' : '保存'}
            </button>
          </>
        )}
      </div>

      {warnings.map((w, i) => (
        <div key={i} style={{ fontSize: '11px', color: '#a83228', background: '#fde8e8', border: '1px solid #f5c0bb', borderRadius: '4px', padding: '5px 10px', marginBottom: '8px' }}>
          ⚠ {w}
        </div>
      ))}

      <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '20px', alignItems: 'start' }}>
        <div>
          <DistributionChart
            rawScores={rawScores}
            threshold={threshold}
            upperLimit={upperLimit}
            direction={criterion.direction}
            percentiles={criterion.percentiles}
            C={C}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr 56px', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '11px', color: '#c9963f', fontWeight: 600 }}>しきい値</label>
            <input type="range" min={0} max={sliderMax} step={sliderStep} className="cal-slider"
              value={threshold}
              onChange={e => setThreshold(parseFloat(e.target.value))}
              style={{ width: '100%', '--cal-fill': '#c9963f', '--cal-pct': `${Math.min(100, (threshold / sliderMax) * 100)}%` }} />
            <input type="number" value={threshold.toFixed(4)} step={sliderStep}
              onChange={e => setThreshold(parseFloat(e.target.value) || 0)}
              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#c9963f', border: `1px solid ${C.line}`, borderRadius: '3px', padding: '2px 4px', background: C.paper2, width: '56px', textAlign: 'right' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr 56px', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '11px', color: '#5b6ba0', fontWeight: 600 }}>上限値</label>
            <input type="range" min={0} max={sliderMax} step={sliderStep} className="cal-slider"
              value={upperLimit}
              onChange={e => setUpperLimit(parseFloat(e.target.value))}
              style={{ width: '100%', '--cal-fill': '#5b6ba0', '--cal-pct': `${Math.min(100, (upperLimit / sliderMax) * 100)}%` }} />
            <input type="number" value={upperLimit.toFixed(4)} step={sliderStep}
              onChange={e => setUpperLimit(parseFloat(e.target.value) || 0)}
              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#5b6ba0', border: `1px solid ${C.line}`, borderRadius: '3px', padding: '2px 4px', background: C.paper2, width: '56px', textAlign: 'right' }} />
          </div>

          <RawScoreSource criterionKey={criterion.key} C={C} />

          <div style={{ marginTop: 'auto', paddingTop: '6px', display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: '6px' }}>
            <span style={{ fontSize: '10px', color: C.inkMuted }}>調整後平均</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '18px', fontWeight: 700,
              color: adjMean >= 0.5 ? '#4a7c59' : adjMean >= 0.2 ? '#c9963f' : '#c0463a' }}>
              {(adjMean * 100).toFixed(1)}%
            </span>
            <span style={{ fontSize: '9px', color: C.inkMuted }}>n={rawScores.length}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function StackedWeightBar({ label, criteria, weights, totalLabel, C }) {
  const total = criteria.reduce((s, c) => s + (parseFloat(weights[c.id]) || 0), 0)
  const BAR_W = 600
  const denom = total > 0 ? total : 1
  let offset = 0
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: C.inkMuted, marginBottom: '4px' }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: Math.abs(total - 1) > 0.001 ? '#c0463a' : '#4a7c59', fontWeight: 700 }}>
          {totalLabel}: {(total * 100).toFixed(1)}%
        </span>
      </div>
      <div style={{ display: 'flex', width: BAR_W, maxWidth: '100%', height: '22px', borderRadius: '4px', overflow: 'hidden', border: `1px solid ${C.line}`, background: C.paper2 }}>
        {criteria.map(c => {
          const w = parseFloat(weights[c.id]) || 0
          const pct = (w / denom) * 100
          const color = CATEGORY_COLORS[c.category] || C.accent
          const seg = (
            <div key={c.id}
              title={`${c.name}: ${(w * 100).toFixed(1)}%`}
              style={{ width: `${pct}%`, background: color, borderRight: '1px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', fontSize: '9px', color: '#fff', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {pct > 5 ? `${(w * 100).toFixed(0)}%` : ''}
            </div>
          )
          offset += pct
          return seg
        })}
      </div>
    </div>
  )
}

function WeightBalanceEditor({ criteria, C }) {
  const initial = () => {
    const d = {}, a = {}
    criteria.forEach(c => { d[c.id] = c.weight_delivery; a[c.id] = c.weight_appeal })
    return { d, a }
  }
  const [{ d: wDelivery, a: wAppeal }, setWeights] = useState(initial)
  const [saving, setSaving] = useState(false)

  const isDirty = criteria.some(c =>
    wDelivery[c.id] !== c.weight_delivery || wAppeal[c.id] !== c.weight_appeal
  )

  function update(score, id, v) {
    setWeights(prev => ({
      d: score === 'delivery' ? { ...prev.d, [id]: v } : prev.d,
      a: score === 'appeal'   ? { ...prev.a, [id]: v } : prev.a,
    }))
  }

  function reset() { setWeights(initial()) }

  function save() {
    setSaving(true)
    const weights = {}
    criteria.forEach(c => {
      weights[c.id] = { delivery: wDelivery[c.id], appeal: wAppeal[c.id] }
    })
    fetch('/evaluation_criteria/update_weights', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      credentials: 'same-origin',
      body: JSON.stringify({ weights, change_reason: '一括重み調整' }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.text().catch(() => '')
          throw new Error(`HTTP ${res.status} ${body.slice(0, 200)}`)
        }
        router.reload({ preserveScroll: true })
      })
      .catch((e) => alert(`保存に失敗しました: ${e.message}`))
      .finally(() => setSaving(false))
  }

  const totalDelivery = criteria.reduce((s, c) => s + (parseFloat(wDelivery[c.id]) || 0), 0)
  const totalAppeal   = criteria.reduce((s, c) => s + (parseFloat(wAppeal[c.id])   || 0), 0)

  return (
    <div style={{ background: C.paper, border: `1px solid ${isDirty ? C.accent : C.line}`, borderRadius: '10px', padding: '16px 20px', marginBottom: '24px', boxShadow: isDirty ? `0 0 0 2px ${C.accent}33, 0 2px 8px rgba(0,0,0,0.08)` : '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: '14px', fontWeight: 700, color: C.ink, margin: 0 }}>重み付けバランス</h2>
        <span style={{ fontSize: '11px', color: C.inkMuted }}>11指標を相対的に調整</span>
        <div style={{ flex: 1 }} />
        {isDirty && (
          <>
            <button onClick={reset}
              style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '4px', border: `1px solid ${C.line}`, background: 'transparent', color: C.inkMuted, cursor: 'pointer' }}>
              リセット
            </button>
            <button onClick={save} disabled={saving}
              style={{ fontSize: '11px', padding: '4px 14px', borderRadius: '4px', border: 'none', background: C.accent, color: '#fff', cursor: saving ? 'default' : 'pointer', fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
              {saving ? '保存中…' : '一括保存'}
            </button>
          </>
        )}
      </div>

      <StackedWeightBar label="お届け重み" criteria={criteria} weights={wDelivery} totalLabel="合計" C={C} />
      <StackedWeightBar label="魅力重み"   criteria={criteria} weights={wAppeal}   totalLabel="合計" C={C} />

      <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'minmax(170px, 1.3fr) 70px 1.2fr 70px 1.2fr 70px', gap: '8px 12px', alignItems: 'center', fontSize: '11px' }}>
        <div style={{ fontWeight: 700, color: C.inkMuted, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}` }}>指標</div>
        <div style={{ fontWeight: 700, color: C.inkMuted, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}`, textAlign: 'right' }}>調整後平均</div>
        <div style={{ fontWeight: 700, color: C.inkMuted, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}` }}>お届け重み</div>
        <div style={{ fontWeight: 700, color: C.inkMuted, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}`, textAlign: 'right' }}>重み付け平均</div>
        <div style={{ fontWeight: 700, color: C.inkMuted, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}` }}>魅力重み</div>
        <div style={{ fontWeight: 700, color: C.inkMuted, paddingBottom: '4px', borderBottom: `1px solid ${C.lineSoft}`, textAlign: 'right' }}>重み付け平均</div>
        {criteria.map(c => {
          const color = CATEGORY_COLORS[c.category] || C.accent
          return (
            <Row key={c.id} c={c} color={color}
              wd={wDelivery[c.id]} wa={wAppeal[c.id]}
              onChangeD={v => update('delivery', c.id, v)}
              onChangeA={v => update('appeal',   c.id, v)}
              C={C} />
          )
        })}
      </div>

      {(Math.abs(totalDelivery - 1) > 0.001 || Math.abs(totalAppeal - 1) > 0.001) && (
        <div style={{ fontSize: '11px', color: '#a83228', background: '#fde8e8', border: '1px solid #f5c0bb', borderRadius: '4px', padding: '5px 10px', marginTop: '10px' }}>
          ⚠ 合計が100%になっていません（お届け: {(totalDelivery * 100).toFixed(1)}% / 魅力: {(totalAppeal * 100).toFixed(1)}%）
        </div>
      )}
    </div>
  )
}

function StepBtn({ onClick, label, color }) {
  return (
    <button onClick={onClick} style={{
      width: '18px', height: '18px', flexShrink: 0, padding: 0, border: `1px solid ${color}44`,
      borderRadius: '3px', background: 'transparent', color, cursor: 'pointer',
      fontSize: '11px', fontWeight: 700, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{label}</button>
  )
}

function Row({ c, color, wd, wa, onChangeD, onChangeA, C }) {
  const adj = c.adjusted_mean
  const wmd = c.weighted_mean_delivery
  const wma = c.weighted_mean_appeal
  const adjColor = adj == null ? C.inkMuted : adj >= 0.5 ? '#4a7c59' : adj >= 0.2 ? '#c9963f' : '#c0463a'
  const fmtPct = v => v == null ? '—' : `${(v * 100).toFixed(1)}%`
  const fmtPt  = v => v == null ? '—' : `${(v * 100).toFixed(2)}pt`
  const step = 0.005
  const clamp = v => parseFloat(Math.min(0.3, Math.max(0, v)).toFixed(4))
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span style={{ color: C.ink, fontWeight: 500 }}>{c.name}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '9px', color: C.inkMuted }}>{c.key}</span>
      </div>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: adjColor, fontWeight: 700, textAlign: 'right' }}>
        {fmtPct(adj)}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <StepBtn onClick={() => onChangeD(clamp(wd - step))} label="−" color={color} />
        <input type="range" min={0} max={0.3} step={step} className="cal-slider"
          value={wd}
          onChange={e => onChangeD(parseFloat(e.target.value))}
          style={{ flex: 1, '--cal-fill': color, '--cal-pct': `${(wd / 0.3) * 100}%` }} />
        <StepBtn onClick={() => onChangeD(clamp(wd + step))} label="＋" color={color} />
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: color, fontWeight: 600, width: '40px', textAlign: 'right' }}>{(wd * 100).toFixed(1)}%</span>
      </div>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.ink, fontWeight: 600, textAlign: 'right' }}>
        {fmtPt(wmd)}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <StepBtn onClick={() => onChangeA(clamp(wa - step))} label="−" color={color} />
        <input type="range" min={0} max={0.3} step={step} className="cal-slider"
          value={wa}
          onChange={e => onChangeA(parseFloat(e.target.value))}
          style={{ flex: 1, '--cal-fill': color, '--cal-pct': `${(wa / 0.3) * 100}%` }} />
        <StepBtn onClick={() => onChangeA(clamp(wa + step))} label="＋" color={color} />
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: color, fontWeight: 600, width: '40px', textAlign: 'right' }}>{(wa * 100).toFixed(1)}%</span>
      </div>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.ink, fontWeight: 600, textAlign: 'right' }}>
        {fmtPt(wma)}
      </div>
    </>
  )
}

function RecomputeButton({ C }) {
  const [status, setStatus] = useState({ state: 'idle' })
  const pollRef = useRef(null)

  useEffect(() => {
    fetch('/evaluation_criteria/recompute_status', { headers: { 'Accept': 'application/json' } })
      .then(r => r.json()).then(s => {
        setStatus(s || { state: 'idle' })
        if (s && s.state === 'running') startPolling()
      })
      .catch(() => {})
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function startPolling() {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(() => {
      fetch('/evaluation_criteria/recompute_status', { headers: { 'Accept': 'application/json' } })
        .then(r => r.json()).then(s => {
          setStatus(s || { state: 'idle' })
          if (s && s.state !== 'running') {
            clearInterval(pollRef.current)
            pollRef.current = null
            if (s.state === 'done') {
              setTimeout(() => router.reload(), 500)
            }
          }
        })
        .catch(() => {})
    }, 2000)
  }

  function start() {
    if (!window.confirm('全SetPlanに対して評価を再計算します。よろしいですか？')) return
    fetch('/evaluation_criteria/recompute_all', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
    }).then(r => r.json()).then(() => {
      setStatus({ state: 'running', total: 0, done: 0, errors: 0 })
      startPolling()
    }).catch(() => {})
  }

  const running = status.state === 'running'
  const pct = running && status.total > 0 ? (status.done / status.total) * 100 : 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', minWidth: '260px' }}>
      <div style={{ display: 'flex', gap: '6px' }}>
        <Link href="/recompute_runs" style={{ fontSize: '11px', color: C.inkMuted, textDecoration: 'none', border: `1px solid ${C.line}`, borderRadius: '4px', padding: '5px 10px' }}>実行履歴</Link>
        <button
          onClick={start}
          disabled={running}
          style={{
            fontSize: '12px', padding: '6px 14px', borderRadius: '4px', border: 'none',
            background: running ? C.lineSoft : C.accent, color: running ? C.inkMuted : '#fff',
            cursor: running ? 'default' : 'pointer', fontWeight: 600,
          }}>
          {running ? '再計算中…' : '過去データ一括再計算'}
        </button>
      </div>
      {(running || status.state === 'done' || status.state === 'error') && (
        <div style={{ width: '260px', fontSize: '10px', color: C.inkMuted }}>
          {running && (
            <>
              <div style={{ width: '100%', height: '6px', background: C.paper2, borderRadius: '3px', overflow: 'hidden', border: `1px solid ${C.line}` }}>
                <div style={{ width: `${pct}%`, height: '100%', background: C.accent, transition: 'width 0.3s' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px', fontFamily: "'JetBrains Mono', monospace" }}>
                <span>{status.done} / {status.total}</span>
                {status.errors > 0 && <span style={{ color: '#c0463a' }}>エラー {status.errors}</span>}
              </div>
            </>
          )}
          {status.state === 'done' && (
            <div style={{ color: '#4a7c59', fontWeight: 600 }}>完了: {status.done}件 {status.errors > 0 ? `(エラー ${status.errors}件)` : ''}</div>
          )}
          {status.state === 'error' && (
            <div style={{ color: '#c0463a' }}>エラー: {status.error}</div>
          )}
        </div>
      )}
    </div>
  )
}

export default function EvaluationCriteriaIndex({ criteria }) {
  const { theme: C } = useTheme()

  const byCategory = {}
  criteria.forEach(c => {
    if (!byCategory[c.category]) byCategory[c.category] = []
    byCategory[c.category].push(c)
  })

  return (
    <AppLayout>
      <style>{`
        input[type="range"].cal-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 6px;
          border-radius: 3px;
          background: linear-gradient(to right,
            var(--cal-fill, #777) 0%,
            var(--cal-fill, #777) var(--cal-pct, 0%),
            #dcdcd6 var(--cal-pct, 0%),
            #dcdcd6 100%);
          outline: none;
          margin: 0;
          padding: 0;
        }
        input[type="range"].cal-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--cal-fill, #777);
          cursor: pointer;
          border: 2px solid #fff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.25);
        }
        input[type="range"].cal-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--cal-fill, #777);
          cursor: pointer;
          border: 2px solid #fff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.25);
        }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '20px', fontWeight: 500, color: C.ink, marginBottom: '6px' }}>指標キャリブレーション</div>
          <p style={{ color: C.inkMuted, fontSize: '13px', margin: 0, maxWidth: '640px' }}>
            各指標の実績スコア分布（グレーのバー）に対して、しきい値（橙の点線）と上限値（青の点線）を調整します。色付きバーがスコアリング有効範囲です。
          </p>
        </div>
        <RecomputeButton C={C} />
      </div>

      <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: C.inkMuted, marginBottom: '20px', flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <svg width={24} height={10}><line x1={0} y1={5} x2={24} y2={5} stroke="#c9963f" strokeWidth={2} strokeDasharray="4,2" /></svg>
          しきい値
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <svg width={24} height={10}><line x1={0} y1={5} x2={24} y2={5} stroke="#5b6ba0" strokeWidth={2} strokeDasharray="4,2" /></svg>
          上限値
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <svg width={12} height={10}><rect x={0} y={0} width={12} height={10} fill="#b0b8c4" rx={1} /></svg>
          スコアリング範囲外のデータ
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <svg width={12} height={10}><rect x={0} y={0} width={12} height={10} fill="#4a7c59" rx={1} /></svg>
          スコアリング範囲内のデータ（加点）
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <svg width={12} height={10}><rect x={0} y={0} width={12} height={10} fill="#c0463a" rx={1} /></svg>
          スコアリング範囲内のデータ（減点）
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <svg width={24} height={10}><line x1={12} y1={0} x2={12} y2={10} stroke="#2c2c2c" strokeWidth={1.5} /></svg>
          平均
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <svg width={24} height={10}>
            <line x1={4} y1={5} x2={4} y2={10} stroke="#888" strokeWidth={1} />
            <line x1={12} y1={5} x2={12} y2={10} stroke="#888" strokeWidth={1} />
            <line x1={20} y1={5} x2={20} y2={10} stroke="#888" strokeWidth={1} />
          </svg>
          p10 / 中央値 / p90
        </span>
      </div>

      <WeightBalanceEditor criteria={criteria} C={C} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
        <h2 style={{ fontSize: '15px', fontWeight: 700, color: C.ink, margin: 0 }}>個別指標の調整</h2>
        <span style={{ fontSize: '11px', color: C.inkMuted }}>しきい値・上限値・分布</span>
      </div>

      {CATEGORY_ORDER.map(cat => (
        <div key={cat} style={{ marginBottom: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: CATEGORY_COLORS[cat] || C.accent }} />
            <h2 style={{ fontSize: '11px', fontWeight: 700, color: CATEGORY_COLORS[cat] || C.inkMuted, textTransform: 'uppercase', letterSpacing: '0.12em', margin: 0 }}>
              {cat}
            </h2>
          </div>
          {(byCategory[cat] || []).map(c => (
            <CriterionCard key={c.id} criterion={c} C={C} />
          ))}
        </div>
      ))}
    </AppLayout>
  )
}
